import {
  getMCPMonitorDistribution,
  getMCPMonitorTopSources,
} from "@/api/endpoints/searches";
import { Source, SOURCE_CODE_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { MCPMonitorTopSources } from "@/types/Search";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
} from "@components/ui/widget";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "@components/widgets/utils";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";

export default function ReachDriverWidget({
  searchUuid,
  className,
  filters,
  ...rest
}: WidgetProps & {
  searchUuid: Uuid;
  filters: SearchFilterV4WithDates;
}) {
  const { abbreviateNumber } = useI18n();

  // Server state
  const { data: topSourcesData, isLoading: isTopSourcesLoading } = useQuery({
    queryKey: [searchUuid, "insights-reach", "reach-driver", filters],
    queryFn: async () => {
      return getMCPMonitorTopSources(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        metric: "reach",
        details: false,
      });
    },
  });

  const { data: topChannelTypesData, isLoading: isTopChannelTypesLoading } =
    useQuery({
      queryKey: [
        searchUuid,
        "insights-reach",
        "channel-type-distribution",
        filters,
      ],
      queryFn: async () => {
        return getMCPMonitorDistribution(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          metric: "reach",
        });
      },
    });

  const isLoading = isTopSourcesLoading || isTopChannelTypesLoading;

  // Helpers
  const topChannelStypesShares = topChannelTypesData?.ok
    ? (Object.entries(topChannelTypesData.data.shares) as [Source, number][])
    : [];
  const topChannelType = topChannelStypesShares.reduce<
    Nullable<[Source, number]>
  >((best, entry) => (!best || entry[1] > best[1] ? entry : best), null)?.[0];

  const sources = topSourcesData?.ok ? topSourcesData.data.sources : [];
  const topSource = sources.reduce<
    Nullable<MCPMonitorTopSources["sources"][number]>
  >(
    (best, source) => (!best || source.value > best.value ? source : best),
    null
  );

  return (
    <Widget className={cn("h-23.5 gap-0.5", className)} {...rest}>
      <WidgetHeader isLoading={isLoading}>
        <WidgetTitle>
          <Trans>Största räckviddsdrivaren</Trans>
        </WidgetTitle>
        {topChannelType ? (
          <WidgetStat>
            {i18n._(SOURCE_CODE_TO_LABEL[topChannelType])}
          </WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent>
        {topSource ? (
          <p className="text-right text-xs text-balance text-muted-foreground">
            {topSource.channel_name} ({abbreviateNumber(topSource.value, 0)})
          </p>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
